import { useState, useEffect, useRef } from 'react';
import {
  Mail, Phone, MapPin, Instagram, Facebook, Twitter, Youtube,
  Linkedin, Music, Calendar, ExternalLink, Play, ChevronRight, ChevronLeft, Star, Quote,
  User, ArrowRight, X, Plus, Briefcase, Users, HelpCircle, MessageSquare, CheckCircle,
  Pause
} from 'lucide-react';

import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AnimatedSection } from './AnimatedSection';

const THEMES = {
  dark_stage: {
    bg: 'bg-slate-900',
    text: 'text-white',
    textMuted: 'text-slate-400',
    card: 'bg-slate-800',
    border: 'border-slate-700',
    accent: 'text-orange-500',
    button: 'bg-orange-500 hover:bg-orange-400',
  },
  theatre_red: {
    bg: 'bg-[#1a0c0e] tx-velvet',
    text: 'text-[#f8f0e6]',
    textMuted: 'text-[#bfa39a]',
    card: 'bg-[#120709] tx-velvet-alt',
    border: 'border-[#3b2125]',
    accent: 'text-[#d4af37]',
    button: 'bg-[#b91c1c] hover:bg-[#dc2626]',
  },
  ivory_gold: {
    bg: 'bg-[#faf7f1]',
    text: 'text-[#1c1710]',
    textMuted: 'text-[#8d7f68]',
    card: 'bg-[#f1ebdd]',
    border: 'border-[#e2d8c3]',
    accent: 'text-[#b08d2f]',
    button: 'bg-[#1c1710] hover:bg-[#3a2f1e]',
  },
  light_modern: {
    bg: 'bg-white',
    text: 'text-slate-900',
    textMuted: 'text-slate-500',
    card: 'bg-slate-50',
    border: 'border-slate-200',
    accent: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-500',
  },
  colorful_kids: {
    bg: 'bg-amber-50',
    text: 'text-purple-900',
    textMuted: 'text-purple-600',
    card: 'bg-white',
    border: 'border-pink-200',
    accent: 'text-pink-500',
    button: 'bg-pink-500 hover:bg-pink-400',
  },
  elegant_classic: {
    bg: 'bg-stone-900',
    text: 'text-stone-50',
    textMuted: 'text-stone-400',
    card: 'bg-stone-800',
    border: 'border-stone-700',
    accent: 'text-amber-400',
    button: 'bg-amber-500 hover:bg-amber-400',
  },
  bold_artist: {
    bg: 'bg-zinc-900',
    text: 'text-white',
    textMuted: 'text-zinc-400',
    card: 'bg-zinc-800',
    border: 'border-zinc-700',
    accent: 'text-red-500',
    button: 'bg-red-500 hover:bg-red-400',
  },
};

// Social icon mapper
const getSocialIcon = (platform) => {
  const name = platform.toLowerCase();
  if (name.includes('instagram')) return Instagram;
  if (name.includes('facebook')) return Facebook;
  if (name.includes('twitter') || name.includes('x')) return Twitter;
  if (name.includes('youtube')) return Youtube;
  if (name.includes('linkedin')) return Linkedin;
  if (name.includes('spotify') || name.includes('music')) return Music;
  return ExternalLink;
};


export const TeamSection = ({ data, theme, primaryColor }) => {
  const t = THEMES[theme] || THEMES.dark_stage;
  const members = data?.members || [];
  const layout = data?.layout || 'grid';
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => e.key === 'Escape' && setSelected(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  if (members.length === 0) return null;

  const renderGridLayout = () => (
    <div className={cn("gap-6", (data.members || []).length <= 2 ? "grid grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto" : "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4")}>
      {members.map((member, idx) => (
        <AnimatedSection
          key={member.id || idx}
          animation="fadeUp"
          delay={idx * 80}
          className={cn("text-center group", member.bio && "cursor-pointer")}
        >
          <div onClick={() => member.bio && setSelected(member)} data-testid={`team-member-${idx}`}>
            <div className={cn("aspect-square rounded-2xl overflow-hidden mb-4 border", t.border)}>
              {member.image ? (
                <img src={member.image} alt={member.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <div className={cn("w-full h-full flex items-center justify-center", t.bg)}>
                  <User className="w-16 h-16" style={{ color: primaryColor + '40' }} />
                </div>
              )}
            </div>
            <h3 className={cn("font-bold", t.text)}>{member.name}</h3>
            <p className="text-sm" style={{ color: primaryColor }}>{member.role}</p>
            {member.bio && (
              <p className="text-xs mt-2 font-semibold uppercase tracking-wider group-hover:underline" style={{ color: primaryColor }}>
                Read Bio +
              </p>
            )}
          </div>
        </AnimatedSection>
      ))}
    </div>
  );

  const renderListLayout = () => (
    <div className="space-y-6 max-w-3xl mx-auto">
      {members.map((member, idx) => (
        <AnimatedSection
          key={member.id || idx}
          animation="slideRight"
          delay={idx * 100}
          className={cn("flex gap-6 p-6 rounded-xl border", t.bg, t.border)}
        >
          <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
            {member.image ? (
              <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
            ) : (
              <div className={cn("w-full h-full flex items-center justify-center", t.card)}>
                <User className="w-10 h-10" style={{ color: primaryColor + '40' }} />
              </div>
            )}
          </div>
          <div>
            <h3 className={cn("font-bold text-lg", t.text)}>{member.name}</h3>
            <p className="text-sm" style={{ color: primaryColor }}>{member.role}</p>
            {member.bio && <p className={cn("text-sm mt-2", t.textMuted)}>{member.bio}</p>}
          </div>
        </AnimatedSection>
      ))}
    </div>
  );

  return (
    <section id="team" className={cn("py-24 px-4", t.bg)}>
      <div className="max-w-6xl mx-auto">
        <AnimatedSection animation="fadeUp" className="text-center mb-16">
          <span className="text-sm uppercase tracking-widest font-semibold mb-4 block" style={{ color: primaryColor }}>{data?.eyebrow || 'Meet The Team'}</span>
          <h2 className={cn("text-4xl md:text-5xl font-bold", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>{data.title || 'The Team'}</h2>
        </AnimatedSection>
        {layout === 'list' ? renderListLayout() : renderGridLayout()}
      </div>

      {/* Bio pop-up */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="team-bio-modal"
        >
          <div
            className={cn("relative max-w-2xl w-full max-h-[85vh] overflow-y-auto rounded-2xl border p-8", t.card, t.border)}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelected(null)}
              className={cn("absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center border", t.border, t.text)}
              data-testid="team-bio-close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-5 mb-6">
              {selected.image && (
                <img src={selected.image} alt={selected.name} className="w-24 h-24 rounded-2xl object-cover flex-shrink-0" />
              )}
              <div>
                <h3 className={cn("text-2xl font-bold", t.text)}>{selected.name}</h3>
                <p className="font-medium" style={{ color: primaryColor }}>{selected.role}</p>
              </div>
            </div>
            <div className={cn("space-y-4 leading-relaxed", t.textMuted)}>
              {selected.bio.split('\n').filter(Boolean).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};


